import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Target } from 'lucide-react';
import { 
  FaJava, FaPython, FaReact,
  FaBootstrap, FaNodeJs, FaGit, FaGithub, FaAws, FaBrain, FaBolt, FaDocker,
  FaShieldAlt, FaProjectDiagram, FaRoute, FaClipboardCheck, FaMagic, FaRobot,
  FaPlug, FaLink, FaBookOpen, FaSitemap, FaChartLine, FaSyncAlt, FaCommentDots
} from 'react-icons/fa';
import { 
  SiSpring, SiMongodb, SiPostman, SiVercel, SiNetlify,
  SiVisualstudiocode, SiEclipseide, SiRender, SiBitbucket, SiGitlab, SiGoogle, SiOracle, SiPostgresql, SiSqlite, SiVite, SiSwagger, SiJirasoftware,
} from 'react-icons/si';
import { GrMysql } from 'react-icons/gr';
import { useTheme } from '../contexts/ThemeContext';
import './Skills.css';
import SectionHeader from './SectionHeader';

// Three bands, ordered by relevance to the current role: AI first, then the engineering
// foundation, then the tooling around it. Inside each group the strongest skills come first.
const bands = [
  {
    id: 'ai',
    index: '01',
    title: 'AI Engineering',
    blurb: 'The frameworks, techniques, and models behind AI systems',
    featured: true,
    groups: [
      { title: 'Frameworks & Systems', skills: [
        { name: 'LangGraph', icon: <FaProjectDiagram />, color: '#0ea5e9' },
        { name: 'LangChain', icon: <FaLink />, color: '#1c3c3c', darkColor: '#9ca3af' },
        { name: 'Multi-Agent Systems', icon: <FaSitemap />, color: '#a855f7' },
        { name: 'Agentic AI', icon: <FaRobot />, color: '#ff6f20' },
        { name: 'MCP', icon: <FaPlug />, color: '#6366f1' },
        { name: 'Guardrails AI', icon: <FaShieldAlt />, color: '#16a34a' },
        { name: 'AutoGen', icon: <FaSyncAlt />, color: '#0078d4' },
        { name: 'Langfuse', icon: <FaChartLine />, color: '#000000', darkColor: '#ffffff' }
      ] },
      { title: 'Techniques', skills: [
        { name: 'RAG', icon: <FaBookOpen />, color: '#8b5cf6' },
        { name: 'Prompt Engineering & Optimization', icon: <FaMagic />, color: '#ec4899' },
        { name: 'Evaluation Harness', icon: <FaClipboardCheck />, color: '#22c55e' },
        { name: 'AI Guardrails', icon: <FaShieldAlt />, color: '#ef4444' },
        { name: 'Intent Classification', icon: <FaRoute />, color: '#14b8a6' },
        { name: 'ReAct', icon: <FaSyncAlt />, color: '#f97316' }
      ] },
      { title: 'Models & Platforms', skills: [
        { name: 'Cohere', icon: <FaCommentDots />, color: '#ff7759' },
        { name: 'Gemma', icon: <SiGoogle />, color: '#4285f4' },
        { name: 'Groq', icon: <FaBolt />, color: '#f55036' },
        { name: 'Hugging Face', icon: <span style={{ fontWeight: 'bold', fontSize: '1.2em' }}>🤗</span>, color: '#ffd21e' },
        { name: 'Machine Learning', icon: <FaBrain />, color: '#667eea' }
      ] }
    ]
  },
  {
    id: 'software',
    index: '02',
    title: 'Software Engineering',
    blurb: 'The full-stack foundation underneath',
    groups: [
      { title: 'Languages', skills: [
        { name: 'Java', icon: <FaJava />, color: '#f89820' },
        { name: 'Python', icon: <FaPython />, color: '#3776ab' }
      ] },
      { title: 'Backend', skills: [
        { name: 'Spring Boot', icon: <SiSpring />, color: '#6db33f' },
        { name: 'Express.js (Node.js)', icon: <FaNodeJs />, color: '#339933' }
      ] },
      { title: 'Frontend', skills: [
        {
          name: 'React + Vite',
          icon: (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.12em', fontSize: '0.78em' }}>
              <FaReact style={{ color: '#61dafb' }} />
              <SiVite style={{ color: '#646cff' }} />
            </span>
          ),
          color: '#61dafb'
        },
        { name: 'Bootstrap', icon: <FaBootstrap />, color: '#7952b3' }
      ] },
      { title: 'Databases', skills: [
        { name: 'MySQL', icon: <GrMysql strokeWidth={1.2} />, color: '#00758f', darkColor: '#5aa9d6' },
        { name: 'Oracle SQL', icon: <SiOracle />, color: '#f80000', darkColor: '#ff0000' },
        { name: 'PostgreSQL', icon: <SiPostgresql />, color: '#336791', darkColor: '#5b9bd1' },
        { name: 'SQLite', icon: <SiSqlite />, color: '#0f80cc' },
        { name: 'MongoDB', icon: <SiMongodb />, color: '#47a248' }
      ] }
    ]
  },
  {
    id: 'platform',
    index: '03',
    title: 'Platform & Tooling',
    blurb: 'Version control, cloud, and the tools that ship it',
    groups: [
      { title: 'Version Control', skills: [
        { name: 'Git', icon: <FaGit />, color: '#f05032' },
        { name: 'GitHub', icon: <FaGithub />, color: '#181717', darkColor: '#ffffff' },
        { name: 'GitLab', icon: <SiGitlab />, color: '#fc6d26' },
        { name: 'Bitbucket', icon: <SiBitbucket />, color: '#0052CC' },
        { name: 'TortoiseSVN', icon: <span style={{ fontWeight: 'bold', fontSize: '1.2em' }}>🐢</span>, color: '#1e6bb8' }
      ] },
      { title: 'Cloud & Deployment', skills: [
        { name: 'OCI', icon: <SiOracle />, color: '#f80000' },
        { name: 'AWS', icon: <FaAws />, color: '#ff9900' },
        { name: 'Vercel', icon: <SiVercel />, color: '#000000', darkColor: '#ffffff' },
        { name: 'Render', icon: <SiRender />, color: '#46E3B7' },
        { name: 'Netlify', icon: <SiNetlify />, color: '#00C7B7' }
      ] },
      { title: 'Tools', skills: [
        { name: 'Docker', icon: <FaDocker />, color: '#2496ed' },
        { name: 'Postman', icon: <SiPostman />, color: '#ff6c37' },
        { name: 'Swagger', icon: <SiSwagger />, color: '#85ea2d' },
        { name: 'VS Code', icon: <SiVisualstudiocode />, color: '#007acc' },
        { name: 'Eclipse', icon: <SiEclipseide />, color: '#2c2255', darkColor: '#8b7fe0' },
        { name: 'Jira Software', icon: <SiJirasoftware />, color: '#0052CC' }
      ] }
    ]
  }
];

const Skills = () => {
  const { isDarkMode } = useTheme();
  const reduceMotion = useReducedMotion();

  // One entrance pattern for the whole page; disabled for reduced-motion users.
  const rise = (delay = 0) =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, amount: 0.15 },
          transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }
        };

  return (
    <section className="skills-section" id="skills">
      <div className="skills-container">
        <SectionHeader
          eyebrow="Toolbox"
          lead="Skills &"
          accent="Technologies"
          subtitle="Wide toolbox, sharp focus: the AI, stacks, and tools I've worked with."
          {...rise()}
        />

        <div className="skills-bands">
          {bands.map((band, bandIndex) => (
            <motion.article
              key={band.id}
              className={`skills-band ${band.featured ? 'is-featured' : ''}`}
              aria-labelledby={`band-${band.id}`}
              {...rise(bandIndex * 0.06)}
            >
              <header className="band-head">
                <span className="band-index" aria-hidden="true">{band.index}</span>
                <div className="band-heading">
                  <h3 id={`band-${band.id}`} className="band-title">{band.title}</h3>
                  <p className="band-blurb">{band.blurb}</p>
                </div>
                {band.featured && (
                  <span className="band-tag">
                    <Target size={14} aria-hidden="true" />
                    Primary focus
                  </span>
                )}
              </header>

              <div className="band-groups">
                {band.groups.map((group) => (
                  <div key={group.title} className="band-group" role="group" aria-label={group.title}>
                    <h4 className="group-title">{group.title}</h4>
                    <ul className="skill-chips">
                      {group.skills.map((skill) => {
                        const color = isDarkMode && skill.darkColor ? skill.darkColor : skill.color;
                        return (
                          <li key={skill.name} className="skill-chip" style={{ '--c': color }}>
                            <span className="chip-icon" style={{ color }}>{skill.icon}</span>
                            <span className="chip-name">{skill.name}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Skills;
